import { apiBaseUrl, apiRequest } from '../../../src/services/api';
import { t } from '../../../src/services/language';
import { FILE_SUFFIX, fileContextFromKey, openFile, sealFile } from '../../../src/shared/contentCrypto';
import { getContentKey } from '../../../src/services/crypto/contentKey';
import { requireUserId } from '../../../src/services/supabase/auth';

/**
 * `storage/s3.ts`'nin web karşılığı — aynı imzalı adres düzeni
 * (`/api/v1/storage/sign`), yalnızca Node akışları yerine tarayıcı fetch'i.
 * Tarayıcıdan depoya (R2) doğrudan PUT/GET gittiği için kovada
 * app.talknote.net kökenine CORS izni tanımlı olmalı (bkz. webapp/README.md).
 */

const TRANSFER_TIMEOUT_MS = 30 * 60_000;

type SignedUrl = { key: string; method: 'GET' | 'PUT'; url: string; contentType?: string };
type SignItem = { method: 'GET'; key: string } | { method: 'PUT'; key: string; size: number };

async function sign(item: SignItem): Promise<SignedUrl> {
  const { urls } = await apiRequest<{ urls: SignedUrl[] }>('POST', '/api/v1/storage/sign', { body: { items: [item] } });
  const signed = urls?.[0];
  if (!signed?.url) throw new Error(t('Depo adresi alınamadı.', 'Could not get a storage URL.'));
  return signed;
}

export function storageInfo(): { configured: boolean; provider: string; bucket?: string } {
  return { configured: isS3Configured(), provider: 'TalkNote bulutu' };
}

export function isS3Configured(): boolean {
  return Boolean(apiBaseUrl());
}

export function s3MediaOrigin(): string | null {
  return null;
}

export interface MeetingKeys {
  audioKey: string;
  transcriptKey: string;
  summaryKey: string;
}

/** `sealed`: içerik şifreleme açık — anahtarlar `.tn1` ekli, putObject içeriği şifreler. */
export function meetingKeys(userId: string, meetingId: string, sealed = false): MeetingKeys {
  const prefix = `u/${userId}/${meetingId}`;
  const suffix = sealed ? FILE_SUFFIX : '';
  return {
    audioKey: `${prefix}/audio.mp3${suffix}`,
    transcriptKey: `${prefix}/transcript.json${suffix}`,
    summaryKey: `${prefix}/summary.json${suffix}`,
  };
}

/** Depoya doğrudan istek (imzalı adres). Yükleme ilerlemesi gerekmiyor. */
export async function transfer(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, { ...init, signal: AbortSignal.timeout(TRANSFER_TIMEOUT_MS) });
  } catch {
    throw new Error(t('Depoya ulaşılamadı.', 'Could not reach storage.'));
  }
  if (!res.ok) throw new Error(t(`Depo isteği başarısız (HTTP ${res.status}).`, `Storage request failed (HTTP ${res.status}).`));
  return res;
}

export async function putObject(key: string, body: Blob | Uint8Array | string, _contentType: string): Promise<void> {
  let blob = body instanceof Blob ? body : new Blob([body as BlobPart]);
  // `.tn1` anahtar: içerik yüklemeden önce kullanıcının anahtarıyla şifrelenir (masaüstüyle aynı).
  const ctx = fileContextFromKey(key);
  if (ctx) {
    const plain = new Uint8Array(await blob.arrayBuffer());
    blob = new Blob([(await sealFile(await getContentKey(await requireUserId()), ctx, plain)) as BlobPart]);
  }
  // İçerik tipini sunucu belirliyor ve imzaya koyuyor; aynısı gönderilmeli.
  const signed = await sign({ method: 'PUT', key, size: blob.size });
  await transfer(signed.url, {
    method: 'PUT',
    headers: { 'Content-Type': signed.contentType ?? 'application/octet-stream' },
    body: blob,
  });
}

export async function putJson(key: string, value: unknown): Promise<void> {
  await putObject(key, JSON.stringify(value), 'application/json; charset=utf-8');
}

/** Şifreli (`…/<ad>.tn1`) dosyayı indirip çözer (masaüstündeki s3.ts ile aynı). */
async function getDecrypted(key: string): Promise<Uint8Array> {
  const ctx = fileContextFromKey(key);
  if (!ctx) throw new Error(t('Şifreli dosya anahtarı tanınmadı.', 'Unrecognised encrypted file key.'));
  const { url } = await sign({ method: 'GET', key });
  const sealed = new Uint8Array(await (await transfer(url, { method: 'GET' })).arrayBuffer());
  return openFile(await getContentKey(await requireUserId()), ctx, sealed);
}

export async function getJson<T>(key: string): Promise<T> {
  if (fileContextFromKey(key)) return JSON.parse(new TextDecoder().decode(await getDecrypted(key))) as T;
  const { url } = await sign({ method: 'GET', key });
  const text = await (await transfer(url, { method: 'GET' })).text();
  if (!text) throw new Error(t('Depodaki dosya boş döndü.', 'The stored file came back empty.'));
  return JSON.parse(text) as T;
}

/** İndirir ve Blob olarak döndürür (arşiv dışa aktarımı). */
export async function getBlob(key: string): Promise<Blob> {
  if (fileContextFromKey(key)) return new Blob([(await getDecrypted(key)) as BlobPart]);
  const { url } = await sign({ method: 'GET', key });
  return (await transfer(url, { method: 'GET' })).blob();
}

export async function presignGet(key: string): Promise<string> {
  return (await sign({ method: 'GET', key })).url;
}

// Çözülmüş seslerin Blob adresleri: aynı dosya tekrar indirilmez, en fazla birkaçı bellekte kalır.
const MEDIA_CACHE = 3;
const mediaUrls = new Map<string, string>();

/**
 * `<audio>`'nun çalabileceği adres. Düz dosyada süreli imzalı adres; şifreli dosyada
 * indirilip tarayıcıda çözülür ve Blob adresinden çalınır (diske yazılmaz).
 */
export async function playableUrl(key: string): Promise<string> {
  if (!fileContextFromKey(key)) return presignGet(key);
  const cached = mediaUrls.get(key);
  if (cached) return cached;
  const url = URL.createObjectURL(new Blob([(await getDecrypted(key)) as BlobPart], { type: 'audio/mpeg' }));
  mediaUrls.set(key, url);
  while (mediaUrls.size > MEDIA_CACHE) {
    const [oldKey, oldUrl] = mediaUrls.entries().next().value as [string, string];
    URL.revokeObjectURL(oldUrl);
    mediaUrls.delete(oldKey);
  }
  return url;
}

export async function downloadToFile(): Promise<void> {
  throw new Error(t('Web uygulamasında desteklenmiyor.', 'Not supported in the web app.'));
}
